import { toast } from "react-toastify";
import { uploadFile, uploadFiles } from "components/util/file_util";
import l from "components/util/l";

// What the clip in the composer offers, WhatsApp style: the user says what kind of thing
// they are sending before any picker opens, because "a document" and "a photo" want
// different dialogs.
const ATTACHMENT_OPTIONS = [
  {
    id: "document",
    icon: "fa-file-lines",
    color: "#7b5cf5",
    accept: "*",
    multiple: true,
    label: () => l("Documento%CH007"),
  },
  {
    id: "image",
    icon: "fa-image",
    color: "#2080e5",
    accept: "image/*,video/*",
    multiple: true,
    label: () => l("Fotos y videos%CH008"),
  },
  {
    id: "audio",
    icon: "fa-headphones",
    color: "#5560c0",
    accept: "audio/*",
    multiple: true,
    label: () => l("Audio%CH019"),
  },
  {
    // Not a picker: `capture` on a file input only reaches the camera on a phone, and a
    // desktop browser opens the explorer instead — so the photo is taken in the page.
    id: "camera",
    icon: "fa-camera",
    color: "#e5407a",
    multiple: true,
    label: () => l("Cámara%CH009"),
  },
  {
    // Not a file either: the browser is asked where the user is, and what travels is a
    // pair of coordinates. The API already speaks this shape — see validate_input's
    // "location" input type.
    id: "location",
    icon: "fa-location-dot",
    color: "#0ea5a5",
    label: () => l("Ubicación%CH023"),
  },
];

// Pointing at the screen is the odd one out: it is not a file picker, and it only makes
// sense where the user has something on screen worth showing. So the chat that wants it
// asks for it, and everything else gets the three pickers.
const SCREENSHOT_OPTION = {
  id: "screenshot",
  icon: "fa-crop-simple",
  color: "#22a06b",
  multiple: true,
  label: () => l("Selección de pantalla%CH010"),
};

const SCREENSHOT_TYPE = "screenshot";
const LOCATION_TYPE = "location";
const CAMERA_OPTION_ID = "camera";

// A FILE field's `file_source`, as the clip option that answers it.
const FILE_SOURCE_OPTION_IDS = {
  EXPLORER: "document",
  CAMERA: CAMERA_OPTION_ID,
  SCREENSHOT: SCREENSHOT_TYPE,
};

// Options that produce something other than a picked file, so the composer knows not to
// treat them as one.
const isLocationAttachment = (attachment) => attachment?.attachment_type == LOCATION_TYPE;
const isScreenshotAttachment = (attachment) => attachment?.attachment_type == SCREENSHOT_TYPE;

const getAttachmentOptions = (showScreenshotAttachment = false) =>
  [...ATTACHMENT_OPTIONS, ...(showScreenshotAttachment ? [SCREENSHOT_OPTION] : [])]
    .map(({ label, ...option }) => ({ ...option, label: label() }));

// A FILE field is always answerable through the explorer, filtered by what the field
// accepts; `file_source` names the option the chat leads with — the one `auto_open` fires.
const getFileRequestOptions = (schema) => {

  const options = getAttachmentOptions(true);
  const multiple = isMultipleAttachment(schema);
  const browseOption = {
    ...options.find((option) => option.id == FILE_SOURCE_OPTION_IDS.EXPLORER),
    accept: getAcceptedFiles(schema),
    multiple,
  };
  const sourceOptionId = FILE_SOURCE_OPTION_IDS[schema.file_source ?? "EXPLORER"];
  const sourceOption = sourceOptionId == browseOption.id
    ? browseOption
    : { ...options.find((option) => option.id == sourceOptionId), multiple };

  return { browseOption, sourceOption };

};

// Free text takes any number of attachments; a FILE field takes one unless it asks for more.
const isMultipleAttachment = (schema) => schema.type != "FILE" || (schema.multiple_files ?? false);

// The Studio stores `accept_files` as a string, sbu and the schema docs as a list.
const getAcceptedFiles = (schema) => [schema.accept_files ?? "*"].flat().join(",");

// Files that reach the composer without a dialog — pasted, dropped, or taken with the
// camera — upload while the send button spins.
const attachFiles = async (files, form, isMultiple = true, flowId) => {

  if (files.length == 0) return;

  form.setFieldValue("attachments_loading", true);
  appendAttachments(form, await uploadFiles(files, flowId), isMultiple);
  form.setFieldValue("attachments_loading", false);

};

// Adds to what is already attached instead of replacing it, reading the current value
// back from the form rather than from the render that started the upload: a second pick
// while this one was in flight already added its own files. A single-file answer keeps
// only the newest: another photo replaces the one before it.
const appendAttachments = (form, attachments, isMultiple = true) => {

  if (attachments.length == 0) return;

  if (!isMultiple) return form.setFieldValue("attachments", attachments.slice(-1));

  const current = form?.values?.attachments;
  form?.setFieldValue("attachments", [
    ...(Array.isArray(current) ? current : []),
    ...attachments,
  ]);

};

// The browser answers with coordinates or with nothing; either way the composer must not
// be left waiting, so a denied permission resolves like a cancelled pick.
const captureLocationAttachment = (form) => new Promise((resolve) => {

  const onFailure = () => {
    toast(l("No se pudo obtener tu ubicación%CH024"), { type: "warning" });
    resolve();
  };

  if (!navigator.geolocation) return onFailure();

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      appendAttachments(form, [{
        attachment_type: LOCATION_TYPE,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
      }]);
      resolve();
    },
    onFailure,
    { enableHighAccuracy: true, timeout: 10000 }
  );

});

const pickAttachmentFiles = (option, form, flowId) =>
  uploadFile({
    flowId,
    multiple: option.multiple,
    accept: option.accept,
    callback: (files) => appendAttachments(form, files, option.multiple),
  });

const captureCameraAttachment = async (takePhoto, form, isMultiple, flowId) => {

  const photo = await takePhoto();

  if (!photo) return;

  await attachFiles([new File([photo], `photo-${Date.now()}.jpg`, { type: "image/jpeg" })], form, isMultiple, flowId);

};

// The screenshot path: the user points at something on screen, writes what is wrong with
// it, and both travel as one attachment. The image is uploaded like any other file so
// everything downstream — the bubble, the model, a form field — reads it the same way.
const captureScreenshotAttachment = async (takeScreenshot, form, isMultiple, flowId) => {

  const result = await takeScreenshot();

  if (!result) return;

  const comment = (result.annotations || "").trim();

  // A capture can fail on an asset that taints the canvas. What the user wrote is worth
  // more than the image, so it goes into the composer instead of being lost.
  if (!result.screenshot) {
    toast(l("No se pudo generar la captura%ES005"), { type: "warning" });
    if (comment) form?.setFieldValue("message", [form?.values?.message, comment].filter(Boolean).join("\n"));
    return;
  }

  form?.setFieldValue("attachments_loading", true);

  const file = new File([result.screenshot], `screenshot-${Date.now()}.png`, { type: "image/png" });
  const uploaded = await uploadFiles([file], flowId);

  appendAttachments(form, uploaded.map((stored) => ({
    ...stored,
    // Read by the API to tell a screenshot from a file the user picked, and to keep the
    // comment next to the image all the way to whatever ends up reading it.
    attachment_type: SCREENSHOT_TYPE,
    annotations: comment,
  })), isMultiple);

  form?.setFieldValue("attachments_loading", false);

};

const onSelectAttachmentOption = (option, form, { takePhoto, takeScreenshot }, flowId) => {
  if (option.id == SCREENSHOT_TYPE) return captureScreenshotAttachment(takeScreenshot, form, option.multiple, flowId);
  if (option.id == LOCATION_TYPE) return captureLocationAttachment(form);
  if (option.id == CAMERA_OPTION_ID) return captureCameraAttachment(takePhoto, form, option.multiple, flowId);
  return pickAttachmentFiles(option, form, flowId);
}

export {
  appendAttachments,
  attachFiles,
  getAttachmentOptions,
  getFileRequestOptions,
  isMultipleAttachment,
  isLocationAttachment,
  isScreenshotAttachment,
  onSelectAttachmentOption,
  LOCATION_TYPE,
  SCREENSHOT_TYPE,
};
